import React, { useContext, FunctionComponent, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

// MUI
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import Toolbar from "@mui/material/Toolbar";

// Custom Components
import GridContainer from "@/ui/Grid/GridContainer";
import FormContent from "@/components/FormContent";
import useFormController from "@/components/useFormController";

import DraggableDialog from "@/ui/Dialog/DraggableDialog";
import DialogHeader from "@/ui/Dialog/DialogHeader";
import DialogToolbarHeading from "@/ui/Dialog/DialogToolbarHeading";
import DialogToolbarButtonClose from "@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose";
import ToolbarFillContent from "@/ui/Toolbar/ToolbarFillContent";
import DialogBody from "@/ui/Dialog/DialogBody";
import DialogActionButtonConfirm from "@/ui/Dialog/ActionButtons/DialogActionButtonConfirm";
import DialogActionButtonCancel from "@/ui/Dialog/ActionButtons/DialogActionButtonCancel";

import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import DialogContext from "@/ui/DialogContext/DialogContext";

// Types
import { ClosingDetails, FormControllerProps } from "@/@types/components/formController";
import { DialogLevel, IDialogProps } from "@/@types/ui/DialogContext";
import { userService } from "@/services/userService";

const PasswordSetDialog: FunctionComponent<IDialogProps> = (props) => {
  const snackbarContext = useContext(SnackbarContext);
  const dialogContext = useContext(DialogContext);

  const { t } = useTranslation();
  const { userId } = props;
  const { record, validation, fields, onFieldChange } = useFormController(
    props as FormControllerProps
  );

  const { onClose } = props;

  const handleConfirm = (evt: SyntheticEvent) => {

    userService.setPassword(userId, record.password_new as string).then((result) => {
      if (result) {
        snackbarContext.showNotification("action", "success");
        close({ success: true }, 2);
      }
    }).catch((err) => {
      if (err)
        snackbarContext.showNotification(err, "error");
    });
  };

  const handleClose = (evt: SyntheticEvent) => {
    close({ success: false });
  };

  const close = (result: any, level?: DialogLevel) => {
    if (onClose) {
      onClose(result);
    }

    dialogContext.hideDialog(level);
  };

  return (
    <DraggableDialog open maxWidth="xs" onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t("titles.password_set")}</DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        <Box m={2}>
          <GridContainer>
            <FormContent
              fieldNames={["password_new", "password_new_confirm"]}
              record={record}
              validation={validation}
              fields={fields}
              onFieldChange={onFieldChange}
              mode="insert"
              columns={1}
            />
          </GridContainer>
        </Box>
      </DialogBody>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        <DialogActionButtonConfirm variant="contained" onClick={handleConfirm} />
      </DialogActions>
    </DraggableDialog>
  );
};

export default PasswordSetDialog;
